/**
 * Simplified solar position calculator.
 * Returns altitude and azimuth of the sun for a given date, latitude, and longitude.
 * Algorithm based on Jean Meeus, "Astronomical Algorithms".
 */

export interface SolarPosition {
	altitude: number; // degrees above horizon (negative = below horizon)
	azimuth: number; // degrees clockwise from north (0=N, 90=E, 180=S, 270=W)
}

export function solarPosition(
	date: Date,
	latitude: number,
	longitude: number,
): SolarPosition {
	const JD = date.getTime() / 86400000 + 2440587.5;
	const n = JD - 2451545.0; // days since J2000.0

	// Mean longitude of the sun (degrees)
	let L = (280.46 + 0.9856474 * n) % 360;
	if (L < 0) L += 360;

	// Mean anomaly (radians)
	let g = (357.528 + 0.9856003 * n) % 360;
	if (g < 0) g += 360;
	const gRad = (g * Math.PI) / 180;

	// Ecliptic longitude (degrees)
	let lambda = L + 1.915 * Math.sin(gRad) + 0.02 * Math.sin(2 * gRad);
	lambda = ((lambda % 360) + 360) % 360;

	// Obliquity of the ecliptic (degrees)
	const epsilon = 23.439 - 0.0000004 * n;

	const lambdaRad = (lambda * Math.PI) / 180;
	const epsRad = (epsilon * Math.PI) / 180;

	// Declination (radians)
	const dec = Math.asin(Math.sin(epsRad) * Math.sin(lambdaRad));

	// Right ascension (radians)
	const RA = Math.atan2(
		Math.cos(epsRad) * Math.sin(lambdaRad),
		Math.cos(lambdaRad),
	);

	// Greenwich Mean Sidereal Time (degrees)
	let GMST = (280.46061837 + 360.98564736629 * n) % 360;
	GMST = ((GMST % 360) + 360) % 360;

	// Local Sidereal Time (degrees)
	const LST = (((GMST + longitude) % 360) + 360) % 360;

	// Hour angle (degrees → radians)
	let HA = LST - (RA * 180) / Math.PI;
	HA = ((((HA + 180) % 360) + 360) % 360) - 180;
	const HARad = (HA * Math.PI) / 180;

	const latRad = (latitude * Math.PI) / 180;

	// Altitude
	const sinAlt =
		Math.sin(latRad) * Math.sin(dec) +
		Math.cos(latRad) * Math.cos(dec) * Math.cos(HARad);
	const altitude =
		(Math.asin(Math.max(-1, Math.min(1, sinAlt))) * 180) / Math.PI;

	// Azimuth
	const cosAlt = Math.cos(Math.asin(Math.max(-1, Math.min(1, sinAlt))));
	let azimuth: number;
	if (cosAlt === 0) {
		azimuth = 0;
	} else {
		azimuth =
			(Math.atan2(
				-Math.sin(HARad),
				Math.tan(dec) * Math.cos(latRad) - Math.sin(latRad) * Math.cos(HARad),
			) *
				180) /
			Math.PI;
	}
	azimuth = ((azimuth % 360) + 360) % 360;

	return { altitude, azimuth };
}

/** Convert degrees to a 16-point compass bearing string like "SW" */
export function compassBearing(degrees: number): string {
	const dirs = [
		"N",
		"NNE",
		"NE",
		"ENE",
		"E",
		"ESE",
		"SE",
		"SSE",
		"S",
		"SSW",
		"SW",
		"WSW",
		"W",
		"WNW",
		"NW",
		"NNW",
	];
	const normalized = ((degrees % 360) + 360) % 360;
	return dirs[Math.round(normalized / 22.5) % 16]!;
}

/** Rainbow viewing direction is opposite the sun (antisolar point + ~42° elevation) */
export function rainbowViewingDirection(sunAzimuth: number): {
	degrees: number;
	bearing: string;
} {
	const opposite = (((sunAzimuth + 180) % 360) + 360) % 360;
	return { degrees: opposite, bearing: compassBearing(opposite) };
}
